/**
 * Flattening/grouping of the aggregate session's branches.
 *
 * An aggregate session sees branches from many brands at once, so every location
 * filter has to be grouped by brand (`<optgroup>` / `SelectGroup`) — two brands
 * routinely have a "Centro" branch and the bare name is ambiguous.
 */

import type { ScopedBrand } from '@/types/auth';

export interface ScopedLocation {
  id: string;
  name: string;
  brandId: string;
  brandName: string;
}

export interface ScopedLocationGroup {
  brandId: string;
  brandName: string;
  locations: ScopedLocation[];
}

/** Every branch of the given brands, brand order preserved, branches sorted by name. */
export function flattenScopedLocations(brands: ScopedBrand[]): ScopedLocation[] {
  return brands.flatMap((brand) =>
    [...brand.locations]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((location) => ({
        id: location.id,
        name: location.name,
        brandId: brand.id,
        brandName: brand.name,
      })),
  );
}

/** Groups a flat scoped-location list back into one group per brand; empty brands are dropped. */
export function groupLocationsByBrand(locations: ScopedLocation[]): ScopedLocationGroup[] {
  const groups = new Map<string, ScopedLocationGroup>();
  for (const location of locations) {
    let group = groups.get(location.brandId);
    if (!group) {
      group = { brandId: location.brandId, brandName: location.brandName, locations: [] };
      groups.set(location.brandId, group);
    }
    group.locations.push(location);
  }
  return [...groups.values()];
}
